"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle, SheetDescription,
} from "@/components/ui/sheet"
import { Icons } from "@/components/icons"

const navItems = [
    { name: "Home", href: "/", scroll: true },
    { name: "Listings", href: "/#listings", scroll: true },
    { name: "Buying", href: "/#buying", scroll: true },
    { name: "Selling", href: "/#selling", scroll: true },
    { name: "Videos", href: "/#videos", scroll: true },
    { name: "Testimonials", href: "/#testimonials", scroll: true },
    { name: "Contact", href: "/#contact", scroll: true },
]

export function MainNav() {
    const pathname = usePathname()
    const [activeSection, setActiveSection] = React.useState("")
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)

    // On the home page, links to its sections scroll smoothly; from any other page the link opens
    // the home page at that section (the /buying, /selling and /listings pages stay reachable from
    // the home page's own buttons).
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        setIsMobileMenuOpen(false) // Close mobile menu after clicking
        if (pathname !== "/") return
        const target = href === "/" ? "#home" : href.replace(/^\//, "")
        if (!target.startsWith("#")) return
        const element = document.querySelector(target)
        if (element) {
            e.preventDefault()
            setActiveSection(href)
            element.scrollIntoView({ behavior: "smooth" })
        }
    }

    const isActive = (href: string) =>
        href.startsWith("/#") || href === "/"
            ? pathname === "/" && activeSection === href
            : pathname === href

    return (
        <nav className="bg-background sticky top-0 z-1001 w-full border-b">
            <div className="flex h-20 items-center justify-between m-1">
                <Link href="/" aria-label="West Coast Home, home" className="h-10 w-28 flex items-center justify-center">
                        <Icons.logo className="h-full w-full text-zinc-500" />
                </Link>
                <div className="hidden md:flex items-center space-x-6 text-sm font-medium md:pr-10 ">
                    {navItems.map((item) => (
                        <Link
                            key={item.href}
                            href={item.href}
                            scroll={item.scroll}
                            onClick={(e) => handleClick(e, item.href)}
                            className={cn(
                                "transition-colors hover:text-foreground/80",
                                isActive(item.href)
                                    ? "text-foreground"
                                    : "text-foreground/60"
                            )}
                        >
                            {item.name}
                        </Link>
                    ))}
                </div>
                <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                    <SheetTrigger asChild>
                        <Button
                            variant="ghost"
                            className="md:hidden"
                            aria-label="Toggle menu"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="3" y1="12" x2="21" y2="12" />
                                <line x1="3" y1="6" x2="21" y2="6" />
                                <line x1="3" y1="18" x2="21" y2="18" />
                            </svg>
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                        <SheetTitle className="hidden">Menu</SheetTitle>
                        <SheetDescription className="hidden"></SheetDescription>
                        <nav className="flex flex-col space-y-4 mt-8">
                            {navItems.map((item) => (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={(e) => handleClick(e, item.href)}
                                    className={cn(
                                        "text-lg font-medium transition-colors hover:text-foreground/80",
                                        isActive(item.href)
                                            ? "text-foreground"
                                            : "text-foreground/60"
                                    )}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </nav>
                    </SheetContent>
                </Sheet>
            </div>
        </nav>
    )
}

